import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchWalletPayments, fetchWalletUser } from "../modules/wallet/services/walletService";
import CancelModal from "../modules/wallet/components/CancelModal";
import { useAccess } from "../context/AccessContext";
import "../css/WalletCancel.css";
import "../css/AdManagement.css";
import "../css/WalletDashboard.css";
import Layout from "./Layout";

const PAGE_SIZE = 30;

const STATUS_LABEL = {
  PAID: "취소가능",
  PARTIALLY_CANCELED: "부분취소",
  CANCELED: "취소완료",
  CANCEL_FAILED: "취소실패",
};

const STATUS_CLASS = {
  PAID: "wc-status-cancelable",
  PARTIALLY_CANCELED: "wc-status-partial",
  CANCELED: "wc-status-canceled",
  CANCEL_FAILED: "wc-status-failed",
};

function formatDateTime(str) {
  if (!str) return "-";
  const d = new Date(str);
  if (isNaN(d)) return str;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_FILTER = {
  userQuery: "",
  orderId: "",
  status: "",
  fromDate: "",
  toDate: "",
};

function WalletCancelManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAccess();
  const mayCancel = hasPermission("wallet.payment.cancel"); // 권한 없으면 취소 버튼 숨김 (R-FE-04)
  const highlightId = location.state?.highlightId ?? null;
  const highlightUserId = location.state?.highlightUserId ?? null;
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ ...EMPTY_FILTER });
  const [tempFilter, setTempFilter] = useState({ ...EMPTY_FILTER });
  const [cancelModalOrderId, setCancelModalOrderId] = useState(null);
  const [cancelModalUserId, setCancelModalUserId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchWalletPayments({ ...filter, page, size: PAGE_SIZE });
        if (!cancelled) {
          setList(result.list || []);
          setTotalCount(result.totalCount || 0);
        }
      } catch {
        if (!cancelled) {
          setList([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [filter, page, refreshKey]);

  async function handleUserClick(memberNo) {
    try {
      const result = await fetchWalletUser(memberNo);
      navigate(`/wallet/user/${result.memberNo}`, { state: { prefetched: result } });
    } catch {
      alert('사용자 정보를 찾을 수 없습니다.');
    }
  }

  function handleSearch() {
    setFilter({ ...tempFilter });
    setPage(1);
  }

  function handleReset() {
    setTempFilter({ ...EMPTY_FILTER });
    setFilter({ ...EMPTY_FILTER });
    setPage(1);
  }

  function handleTempChange(e) {
    const { name, value } = e.target;
    setTempFilter((prev) => ({ ...prev, [name]: value }));
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startIndex = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <Layout>
      <div className="wallet-cancel">
        <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>취소/원복 관리</h4>

        <div className="wallet-cancel-filter">
          <input
            type="text"
            name="userQuery"
            placeholder="사용자 검색"
            value={tempFilter.userQuery}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 160 }}
          />
          <input
            type="text"
            name="orderId"
            placeholder="주문ID"
            value={tempFilter.orderId}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 120 }}
          />
          <select
            name="status"
            value={tempFilter.status}
            onChange={handleTempChange}
            style={{ width: 130 }}
          >
            <option value="">전체</option>
            <option value="PAID">취소가능</option>
            <option value="PARTIALLY_CANCELED">부분취소</option>
            <option value="CANCELED">취소완료</option>
          </select>
          <input
            type="date"
            name="fromDate"
            className="wallet-date-input"
            value={tempFilter.fromDate}
            onChange={handleTempChange}
          />
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>~</span>
          <input
            type="date"
            name="toDate"
            className="wallet-date-input"
            value={tempFilter.toDate}
            onChange={handleTempChange}
          />
          <button className="wallet-filter-action-btn wallet-filter-search" onClick={handleSearch}>
            검색
          </button>
          <button className="wallet-filter-action-btn wallet-filter-reset" onClick={handleReset}>
            초기화
          </button>
        </div>

        <div className="wallet-cancel-table-section">
          {loading ? (
            <div className="text-center text-muted py-4">불러오는 중...</div>
          ) : list.length === 0 ? (
            <div className="wallet-cancel-empty">조회된 데이터가 없습니다</div>
          ) : (
            <>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>접수일</th>
                    <th>상품명</th>
                    <th>사용자</th>
                    <th>결제금액</th>
                    <th>EP 차감</th>
                    <th>CP 차감</th>
                    <th>상태</th>
                    <th>취소</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => (
                    <tr
                      key={item.orderId}
                      className={(highlightId !== null && highlightId === item.orderId) || (highlightUserId !== null && highlightUserId === item.memberNo) ? "wc-row-highlight" : ""}
                    >
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td>{item.productName}</td>
                      <td>
                        <button
                          onClick={() => handleUserClick(item.memberNo)}
                          style={{ background: 'none', border: 'none', padding: 0, color: '#6366f1', cursor: 'pointer', fontSize: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                        >
                          {item.userName}({item.memberNo})
                        </button>
                      </td>
                      <td>{item.totalAmount != null ? item.totalAmount.toLocaleString() + " 원" : "-"}</td>
                      <td>{item.epDeducted != null ? item.epDeducted.toLocaleString() + " P" : "-"}</td>
                      <td>{item.cpDeducted != null ? item.cpDeducted.toLocaleString() + " P" : "-"}</td>
                      <td>
                        <span className={`wc-status-badge ${STATUS_CLASS[item.status] || ""}`}>
                          {STATUS_LABEL[item.status] || item.status}
                        </span>
                      </td>
                      <td>
                        {mayCancel && item.cancelable ? (
                          <button
                            className="wc-cancel-btn"
                            onClick={() => { setCancelModalOrderId(item.orderId); setCancelModalUserId(item.memberNo); }}
                          >
                            취소
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pagination-info">
                <div>총 {totalCount}개 항목 중 {startIndex} - {endIndex}</div>
                <div className="pagination">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    이전
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={page === i + 1 ? "active" : ""}
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={endIndex >= totalCount}>
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {showToast && (
          <div className="wc-success-toast">환불이 완료되었습니다.</div>
        )}

        <CancelModal
          isOpen={cancelModalOrderId !== null}
          orderId={cancelModalOrderId}
          userId={cancelModalUserId}
          onClose={() => { setCancelModalOrderId(null); setCancelModalUserId(null); }}
          onSuccess={() => {
            setCancelModalOrderId(null);
            setCancelModalUserId(null);
            setRefreshKey((k) => k + 1);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
          }}
        />
      </div>
    </Layout>
  );
}

export default WalletCancelManagement;
